<template>
  <BaseModal
    :is-open="isOpen"
    title="계좌비밀번호"
    :show-default-footer="false"
    :close-on-overlay-click="false"
    :close-on-escape="true"
    content-padding="none"
    @close="handleClose"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <div class="px-padding-36 flex flex-col justify-center py-10">
      <div class="gap-size-12 flex flex-col">
        <p class="text-font-18 font-medium">계좌 비밀번호 입력</p>
        <p class="text-font-14">안전한 거래를 위해 비밀번호 확인이 필요해요</p>
      </div>

      <div class="gap-size-12 mt-size-36 flex flex-col">
        <BasePIN :pin="password" @key-press="handleKeyPress" :key="accountPasswordKey">
          <template v-if="passwordFailedCount > 0" #content>
            <p class="mt-size-8 text-font-14 text-[var(--input-color-text-error)]">
              비밀번호가 일치하지 않습니다 ({{ passwordFailedCount }} / 10)
            </p>
            <p
              v-if="isDemoMode()"
              class="mt-size-4 text-font-14 text-[var(--input-color-text-error)]"
            >
              모의계좌 비밀번호는 "000000" 입니다
            </p>
            <a
              v-if="!isDemoMode()"
              class="mb-size-24 text-font-14 cursor-pointer text-[var(--button-tab-menu-text-off)] underline"
              @click="openPasswordResetDialog"
            >
              계좌 비밀번호를 잊으셨나요?
            </a>
          </template>
        </BasePIN>
      </div>

      <button
        class="p-padding-16 bg-bg-bg-innerframe mt-size-12 rounded-[10px] border border-2 border-bg-bg-outline"
        :class="{ 'bg-primary-primary050 border-bg-primary': isAgree }"
        @click="isAgree = !isAgree"
      >
        <div class="gap-size-8 flex items-center">
          <div
            v-if="isAgree"
            class="w-size-20 h-size-20 bg-bg-primary flex items-center justify-center overflow-hidden rounded-full"
          >
            <BaseIcon name="check-sm" color="white" />
          </div>
          <div
            v-else
            class="w-size-20 h-size-20 bg-bg-bg-default rounded-full border border-[1.5px] border-bg-bg-outline"
          ></div>
          <p class="text-font-16 text-default font-bold">빠른 거래</p>
          <BaseChip
            class="bg-bg-primary text-default text-font-12 font-medium"
            label="추천"
            size="sm"
          />
        </div>
        <p class="text-font-13 ml-[30px] text-left">한 번만 입력하면 안전하게 거래할 수 있어요</p>
        <ul class="text-default-muted-dark text-font-12 ml-[46px] list-disc text-left">
          <li class="">계좌비밀번호 재입력 없이 거래 가능</li>
          <li>선택하지 않을 경우, 주문시마다 계좌비밀번호 입력 필요</li>
        </ul>
      </button>
    </div>
    <!-- 푸터 영역 -->
    <template #footer>
      <div class="flex items-center justify-center gap-3">
        <BaseButton
          variant="contained"
          color="primary"
          label="확인"
          :isLoading="isSubmitting"
          :disabled="isConfirmDisabled || passwordFailedCount >= 10 || isSubmitting"
          @click="handleAgree"
          fullWidth
        />
        <BaseButton variant="outlined" color="white" label="취소" @click="handleClose" fullWidth />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { BaseModal, BaseButton, BasePIN, BaseIcon, BaseChip } from '@template/ui';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { computed, onMounted, ref, watch } from 'vue';
import { useAccountStore } from '@template/stores';
import { isDemoMode } from '@template/utils';

const isOpen = defineModel<boolean>('isOpen', { required: true });
const isAgree = ref(false);
const password = ref<string[]>([]);
const isSubmitting = ref(false);
interface Emits {
  (e: 'close'): void;
  (e: 'confirm', isAgree: boolean, password: string): void;
  (e: 'update:isOpen', value: boolean): void;
}

const accountPasswordKey = ref(0);
const accountStore = useAccountStore();
const globalDialogManager = useGlobalDialogManager();

const passwordFailedCount = computed(() => {
  return accountStore.accountPasswordFailedCount;
});

const emit = defineEmits<Emits>();

const isConfirmDisabled = computed(() => {
  return password.value.length !== 6;
});
const handleClose = () => {
  password.value = [];
  isAgree.value = false;
  isSubmitting.value = false;
  emit('close');
  emit('update:isOpen', false);
};

const handleAgree = () => {
  if (password.value.length === 6) {
    isSubmitting.value = true;
    const passwordValue = password.value.join('');
    const isAgreeValue = isAgree.value;

    emit('confirm', isAgreeValue, passwordValue);
  }
};

const KEYPAD_SPECIAL_KEYS = {
  BACKSPACE: 'BACKSPACE',
  EMPTY: 'EMPTY',
} as const;

const handleKeyPress = (key: string) => {
  if (key === KEYPAD_SPECIAL_KEYS.BACKSPACE) {
    password.value = password.value.slice(0, -1);
  } else {
    password.value.push(key);
  }
};

const openPasswordResetDialog = () => {
  globalDialogManager.open('accountPasswordResetConfirm');
};

watch(passwordFailedCount, (newVal) => {
  password.value = [];
  // 비밀번호 실패 시 다시 입력할 수 있도록 로딩 상태 해제
  accountPasswordKey.value++;
  isSubmitting.value = false;
});

onMounted(() => {
  accountStore.resetAccountPasswordFailedCount();
});
</script>
